import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
// Components
import SvgIcon from '../icons/SvgIcon';
import InfoBoxTex from '../infoBoxText';
import Text from '../text';
import TextCenterButton from '../buttons/textCenterButton';
import Separator from '../layout/separator';
import { IconAxel } from '../../resources/images';
import { DropInAdyen } from '../../navigation/routes/payment/dropinAdyen';
import Loader from '../loader';
// Resources
import { stringsRes } from '../../resources/strings';
import { isWebView, sendMessageToNativeApp, WebViewChannel } from '../../utils/webview';
import { asyncStorageApi, KEY_LOCAL_STORAGE_ONBOARDING_DONE } from '../../localStorageAPI';
import { useGetPaymentGroupSettings } from '../../core/services/payments';
import { PaymentGroupSetting } from '../../core/apiTypes';

interface OnboardingStep {
    title: string;
    info?: string;
    action?: string;
    icon?: IconAxel;
    view?: ReactElement;
    onAction?: () => void;
}

interface PropsFromModal {
    onRequestClose?: () => void;
}

interface OnboardingProps extends PropsFromModal {
    onConfirmAction?: () => void;
    onCancelAction?: () => void;
}

const Onboarding: FC<OnboardingProps> = (props): ReactElement => {
    const { onRequestClose, onConfirmAction } = props;
    const classes = useStyle();
    const isNativeApp = isWebView();

    // isLoading, not isFetching: isFetching also goes true on background refetches (reconnect,
    // invalidation), and swapping the step body for the loader unmounts the Adyen drop-in of step 2
    // mid-mount. We only want the loader while there is nothing to show yet.
    const { data: dataB2C, isLoading: isLoadingB2C } = useGetPaymentGroupSettings<PaymentGroupSetting[]>({
        enabled: true,
        variables: {},
    });

    const skipCardAdd = dataB2C?.some((card) => !!card.default_payment_method);

    const [currentStep, setCurrentStep] = useState<number>(0);

    const steps: OnboardingStep[] = [
        {
            title: stringsRes.onboarding.step1.title,
            action: stringsRes.onboarding.step1.action,
            icon: IconAxel.StepWelcome,
        },
        {
            title: stringsRes.onboarding.step2.title,
            info: stringsRes.onboarding.step2.info,
            view: (
                <DropInAdyen
                    inOnboarding
                    skinny
                    onSuccess={() => nextStep()}
                    onError={() => setCurrentStep((step) => step - 1)}
                />
            ),
        },
        {
            title: stringsRes.onboarding.step3.title,
            info: stringsRes.onboarding.step3.info,
            icon: IconAxel.StepGeolocation,
            onAction: () => {
                sendMessageToNativeApp(WebViewChannel.ASK_SETTING_LOCATION, {});
            },
            action: stringsRes.onboarding.step3.action,
        },
        {
            title: stringsRes.onboarding.step4.title,
            info: stringsRes.onboarding.step4.info,
            icon: IconAxel.StepNotification,
            onAction: () => {
                sendMessageToNativeApp(WebViewChannel.ASK_SETTING_NOTIFICATION, {});
            },
            action: stringsRes.onboarding.step4.action,
        },
        {
            title: stringsRes.onboarding.step5.title,
            action: stringsRes.onboarding.step5.action,
            icon: IconAxel.StepFinal,
        },
    ];

    // Remove unnecessary steps for desktop
    if (!isNativeApp) {
        steps.splice(2, 2);
    }
    if (skipCardAdd) {
        steps.splice(1, 1);
    }

    const currentStepInfo = steps[currentStep];

    const onAction = (): void => {
        currentStepInfo.onAction?.();
    };

    const nextStep = useCallback((): void => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onConfirmAction?.();
            onRequestClose();
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_ONBOARDING_DONE, 1);
        }
    }, [currentStep, onConfirmAction, onRequestClose, steps.length]);

    useEffect(() => {
        window.nativeOnBoardingNext = () => {
            console.log('Application nativeApp ask to go to next step');
            nextStep();
        };
    }, [nextStep]);

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <div className={classes.titleContainer}>
                    <Text.H1 text={currentStepInfo.title} className={classes.title} />
                    {/* <span onClick={onClickCancel} className={classes.closeIcon}>
                        <SvgIcon icon={IconCommon.Close} color="black" size="large" />
                    </span> */}
                </div>
            </div>
            {isLoadingB2C ? (
                <Loader />
            ) : (
                <>
                    {currentStepInfo.icon && (
                        <div className={classes.logoContainer}>
                            <SvgIcon icon={currentStepInfo.icon} size="inherit" stroke="none" />
                        </div>
                    )}

                    {currentStepInfo.view ? (
                        <>
                            {currentStepInfo.info && (
                                <InfoBoxTex titleIcon={'info'} description={currentStepInfo.info} />
                            )}
                            {currentStepInfo.view}
                        </>
                    ) : (
                        <>
                            {currentStepInfo.info && (
                                <InfoBoxTex titleIcon={'info'} description={currentStepInfo.info} />
                            )}
                            <Separator height={1} />
                        </>
                    )}

                    <div className={classes.buttonContainer}>
                        {currentStepInfo.action && (
                            <TextCenterButton
                                id="next"
                                title={currentStepInfo.action}
                                onClick={currentStepInfo.onAction ? onAction : nextStep}
                            />
                        )}
                        {currentStep !== 0 && currentStep !== steps.length - 1 && (
                            <TextCenterButton
                                id="cancel"
                                title={stringsRes.common.later}
                                onClick={nextStep}
                                customColor="grey"
                            />
                        )}
                    </div>
                    <div className={classes.stepContainer}>
                        <Text.ShortRegularText14 text={`${currentStep + 1} / ${steps.length}`} />
                    </div>
                </>
            )}
        </div>
    );
};

const useStyle = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        titleContainer: {
            display: 'flex',
            placeItems: 'center',
            justifyContent: 'space-between',
        },
        title: {
            fontSize: 26,
        },
        // closeIcon: {
        //     alignSelf: 'baseline',
        // },
        logoContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
        },
        buttonContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 12,
        },
        stepContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
    }),
);

export default Onboarding;
